import { CardWrapper } from './card-wrapper';
import { atom, useAtomValue } from 'jotai';
import { Heading, Alert } from '@restworld/ui-ds-base';
import { BriefcaseIcon, BuildingOfficeIcon, UserIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { workerPreferencesAtom } from '../global-state.worker-data';
import { WorkerResearchInfo } from '@restworld/utility-types';
import { PresentationCard } from '@restworld/ui-components';
import { useProfileCommonIntl } from './hooks';

const restaurantTypesAtom = atom((get) => {
  const workerResearchInfo = get(workerPreferencesAtom)?.research_info as unknown as WorkerResearchInfo;
  const restaurantTypes = (workerResearchInfo?.restaurant_types || [])
    .map((restaurantType) => restaurantType?.restaurant_type_var?.label)
    .filter((restaurantType) => !!restaurantType);
  return restaurantTypes.length > 0 ? restaurantTypes.join(', ') : undefined;
});

const workerRolesAtom = atom((get) => {
  const workerResearchInfo = get(workerPreferencesAtom)?.research_info as unknown as WorkerResearchInfo;
  const roles = (workerResearchInfo?.occupations || [])
    .map((occupation) => occupation?.label_it?.label)
    .filter((occupation) => !!occupation);
  return roles.length > 0 ? roles.join(', ') : undefined;
});

const workerShiftsAtom = atom((get) => {
  const workerShifts = get(workerPreferencesAtom)?.shifts;
  const shifts = (workerShifts || []).map((shift) => shift?.shift_var?.label).filter((shift) => !!shift);
  return shifts || undefined;
});

const jobTypeAtom = atom((get) => {
  const jobTypes = get(workerPreferencesAtom)?.workerJobTypes;
  return jobTypes;
});

export const WorkerPreferenceSection = () => {
  const { missingInfoText, translate } = useProfileCommonIntl();
  const workerPreference = useAtomValue(workerPreferencesAtom);
  const isInformationMissing = false;
  const errorMessage = (
    <Heading size={5} color="error">
      {translate('common:missing_info')}
    </Heading>
  );
  const sector = (workerPreference?.research_info as unknown as WorkerResearchInfo)?.domain_interest_var?.label;
  const location = useAtomValue(restaurantTypesAtom);
  const role = useAtomValue(workerRolesAtom);
  const shifts = useAtomValue(workerShiftsAtom);
  const jobTypes = useAtomValue(jobTypeAtom);

  return (
    <CardWrapper title={translate('common:preferences')} editPageType="preferences">
      <div id="preferences" className="flex flex-col items-start gap-4 pt-4">
        <Heading size={5} color="muted-foreground" className="lg:max-w-[446px]">
          {translate('onboarding-page:enter_preferences_next_job')}
        </Heading>
        {isInformationMissing && <Alert text={missingInfoText} />}
        <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-8 lg:gap-y-4 gap-4 w-full">
          <PresentationCard
            label={translate('common:sector')}
            header={sector || errorMessage}
            icon={<BriefcaseIcon className="w-6 h-6" />}
            isError={!sector}
          />
          <PresentationCard
            label={translate('common:local')}
            header={location || errorMessage}
            icon={<BuildingOfficeIcon className="w-6 h-6" />}
            isError={!location}
          />
          <PresentationCard
            label={translate('common:role')}
            header={role || errorMessage}
            isError={!role}
            icon={<UserIcon className="w-6 h-6" />}
          />
        </div>
        <div className="flex flex-col gap-4 items-start w-full mt-6">
          <Heading size={5} weight="medium">
            {translate('common:working_hours')}
          </Heading>
          <div className="grid lg:grid-cols-2 grid-cols-1 gap-4 w-full">
            {shifts.map((shift, index) => (
              <div key={index} className="flex flex-row gap-2 items-center justify-start">
                <CheckCircleIcon className="w-6 h-6 min-w-[24px] text-primary-700" />
                <Heading size={5} weight="normal" color="muted-foreground">
                  {shift}
                </Heading>
              </div>
            ))}
            {shifts.length === 0 && errorMessage}
          </div>
        </div>
        <div className="flex flex-col gap-4 items-start w-full mt-6">
          <Heading size={5} weight="medium">
            {translate('common:work_commitment')}
          </Heading>
          <div className="grid lg:grid-cols-2 grid-cols-1 gap-4 w-full">
            {jobTypes
              ? jobTypes.map((job, index) => (
                  <div key={index} className="flex flex-row gap-2 items-center justify-start">
                    <CheckCircleIcon className="w-6 h-6 min-w-[24px] text-primary-700" />
                    <Heading size={5} weight="normal" color="muted-foreground">
                      {job.job_type_var.label}
                    </Heading>
                  </div>
                ))
              : null}
            {!jobTypes?.length && errorMessage}
          </div>
        </div>
      </div>
    </CardWrapper>
  );
};
